import { formSchemas } from "./authoring"
import { getSchemaSectionTitle } from "utilities/authoringFunctions.js"
import { UserBadge } from "components-design-system"

// cell renderers
import {
  FormsCellRenderer,
  LinkCellRenderer,
} from "components/ag-grid-cell-renderers/SubmissionCellRenderers/SubmissionCellRenderers"
import DocumentsUploadStatus from "components/ag-grid-cell-renderers/DocumentsUploadStatus/DocumentsUploadStatus"
import PrsUploadStatusCell from "components/ag-grid-cell-renderers/PrsUploadStatusCell/PrsUploadStatusCell"

// utils
import { dateColDef } from "components/grids/columnTemplates"

const getSubmissionSchemas = () => {
  let filteredSchemas = {}
  // iterate through schemas
  for (const sectKey in formSchemas) {
    const origProps = formSchemas[sectKey].properties
    let filteredProps = null
    for (const regKey in origProps) {
      // filter out "global" registries
      if (regKey !== "global") {
        filteredProps = filteredProps || {}
        filteredProps[regKey] = origProps[regKey]
      }
    }
    // filter out schemas with only "global" registries
    if (filteredProps) {
      filteredSchemas[sectKey] = { ...formSchemas[sectKey] }
      filteredSchemas[sectKey].properties = filteredProps
    }
  }
  return filteredSchemas
}

/**
 * @returns array of form sections in format { id, text }
 */
const getSubmissionFormSections = () => {
  return Object.keys(getSubmissionSchemas()).map((sectId) => {
    return { id: sectId, text: getSchemaSectionTitle(sectId) }
  })
}

/**
 * returns object in format:
 *     { protocl: "Protocol",
 *       results: "Results" }
 * "global" not included
 */
const getSubmissionSectionTitles = () => {
  let sections = {}
  const submissionSchemas = getSubmissionSchemas()
  for (const sectKey in submissionSchemas) {
    sections[sectKey] = submissionSchemas[sectKey].title
  }
  return sections
}

/**
 * returns object in format:
 *     { ctg: "CTG",
 *       eudra: "EudraCT" }
 * "global" not included
 */
const getSubmissionRegistryTitles = () => {
  let registries = {}
  const submissionSchemas = getSubmissionSchemas()
  for (const sectKey in submissionSchemas) {
    const regItems = submissionSchemas[sectKey].properties
    for (const regKey in regItems) {
      if (regKey !== "global") registries[regKey] = regItems[regKey].title
    }
  }
  return registries
}

const getDisplayValue = (form) => {
  const view = FORM_VIEW_TRANSFORMER?.[form?.form_view] || form?.form_view || ""
  const section = FORM_SECTION_TRANSFORMER?.[form?.form_section] || form?.form_section || ""
  return `${view} ${section}`.trim()
}

const isValidForm = (form) => !!registryContinentMap?.[form?.form_view]

const getFirstSortedDisplayValue = (forms) => {
  if (!Array.isArray(forms)) return ""
  const validForms = forms.filter(isValidForm)
  if (!validForms.length) return ""
  const sorted = validForms.sort((a, b) => getDisplayValue(a).localeCompare(getDisplayValue(b)))
  return getDisplayValue(sorted[0])
}

const getSubmissionFormColDefs = () => {
  return {
    headerName: "Form(s)",
    field: "form_types",
    valueGetter: ({ data }) => data?.form_types || [],
    cellRenderer: FormsCellRenderer,
    filterParams: {
      keyCreator: ({ value }) => {
        if (!isValidForm(value)) return "(Blanks)"
        return getDisplayValue(value)
      },
      valueFormatter: ({ value }) => {
        if (!isValidForm(value)) return "(Blanks)"
        return getDisplayValue(value)
      },
      cellClass: ["col-badge"],
    },
    comparator: (a, b) => {
      const valA = getFirstSortedDisplayValue(a)
      const valB = getFirstSortedDisplayValue(b)
      return valA.localeCompare(valB)
    },
  }
}

const REGISTRY_OPTIONS = {
  ctg: {
    value: "ctg",
    label: "CTG",
    views: [
      {
        value: "Protocol",
        label: "Protocol",
        formTypes: [
          {
            form_view: "CTG",
            form_section: "Protocol",
          },
        ],
      },
      {
        value: "Results",
        label: "Results",
        formTypes: [
          {
            form_view: "CTG",
            form_section: "Results",
          },
        ],
      },
      {
        value: "Protocol & Results",
        label: "Protocol & Results",
        formTypes: [
          {
            form_view: "CTG",
            form_section: "Protocol",
          },
          {
            form_view: "CTG",
            form_section: "Results",
          },
        ],
      },
      {
        value: "Delayed Results Certification",
        label: "Delayed Results Certification",
        formTypes: [
          {
            form_view: "CTG",
            form_section: "Delayed_Results_Certification",
          },
        ],
      },
      {
        value: "Protocol & Delayed Results Certification",
        label: "Protocol & Delayed Results Certification",
        formTypes: [
          {
            form_view: "CTG",
            form_section: "Protocol",
          },
          {
            form_view: "CTG",
            form_section: "Delayed_Results_Certification",
          },
        ],
      },
    ],
  },
  eupas: {
    value: "eupas",
    label: "EMA RWD",
    views: [
      {
        formTypes: [
          {
            form_view: "EMA RWD",
            form_section: "Protocol",
          },
        ],
      },
    ],
    formSection: {
      value: "protocol",
      label: "Protocol",
    },
  },
  ctis: {
    value: "ctis",
    label: "CTIS",
    views: [
      {
        value: "Protocol",
        label: "Protocol",
        formTypes: [
          {
            form_view: "CTIS",
            form_section: "Protocol",
          },
        ],
      },
      {
        value: "Results",
        label: "Results",
        formTypes: [
          {
            form_view: "CTIS",
            form_section: "Results",
          },
        ],
      },
      // {
      //   form_view: "CTIS",
      //   form_section: "Protocol"
      // }
    ],
    // formSection: {
    //   value: "protocol",
    //   label: "Protocol"
    // }
  },
  jrct: {
    value: "jrct",
    label: "JRCT",
    views: [
      {
        value: "Protocol",
        label: "Protocol",
        formTypes: [
          {
            form_view: "JRCT",
            form_section: "Protocol",
          },
        ],
      },
      {
        value: "Results",
        label: "Results",
        formTypes: [
          {
            form_view: "JRCT",
            form_section: "Results",
          },
        ],
      },
    ],
  },
  eudra: {
    value: "eudra",
    label: "EudraCT",
    views: [
      {
        formTypes: [
          {
            form_view: "EUDRA",
            form_section: "Results",
          },
        ],
      },
    ],
    formSection: {
      value: "results",
      label: "Results",
    },
  },
}

const FORM_VIEW_TRANSFORMER = {
  EUDRA: "EudraCT",
  EUDRACT: "EudraCT",
}

const FORM_SECTION_TRANSFORMER = {
  Delayed_Results_Certification: "Delayed Results Certification",
}

const registryContinentMap = {
  Global: "global",
  CTG: "americas",
  EUPAS: "europe",
  "EMA RWD": "europe",
  CTIS: "europe",
  EUDRACT: "europe",
  EUDRA: "europe",
  EudraCT: "europe",
  JRCT: "asia",
}

const formsFilterFormatter = ({ value }) => {
  if (!value?.form_view || !value?.form_section) return "(Blanks)"
  return `${FORM_VIEW_TRANSFORMER?.[value?.form_view] || value?.form_view} ${
    FORM_SECTION_TRANSFORMER?.[value?.form_section] || value.form_section
  }`
}

const submissionColDefs = [
  {
    headerName: "Study ID",
    field: "study_id",
  },
  {
    headerName: "Date",
    field: "created_at",
    ...dateColDef,
    suppressSizeToFit: true,
  },
  {
    headerName: "Type",
    field: "download_only", // true || false
    valueGetter: ({ data }) => (data?.download_only ? "Download" : "Submission"),
  },
  {
    headerName: "User",
    field: "created_by_fullname",
    filterParams: {
      keyCreator: ({ value }) => (value?.trim() ? value : "(Blanks)"),
      valueFormatter: ({ value }) => (value?.trim() ? value : "(Blanks)"),
    },
    cellRenderer: ({ value }) => <UserBadge user={value} />,
  },
  getSubmissionFormColDefs(),
  {
    headerName: "Version",
    field: "form_version",
    filter: "agSetColumnFilter",
    valueGetter: ({ data }) => {
      const version = data?.form_version
      return typeof version === "number" && version >= 0 ? version.toString() : "N/A"
    },
    cellRenderer: ({ value }) => value,
    comparator: (a, b) => {
      const valA = a === "N/A" ? -Infinity : parseFloat(a)
      const valB = b === "N/A" ? -Infinity : parseFloat(b)
      return valA - valB
    },
  },
  {
    headerName: "Title of study",
    field: "study_title",
  },
  {
    headerName: "Reason for submission",
    field: "submission_reason",
    valueGetter: ({ data }) => (data?.submission_reason ? data.submission_reason : "N/A"),
  },
  {
    headerName: "File(s) submitted",
    field: "uploaded_docs",
    filterParams: {
      keyCreator: ({ value }) => (value ? value : "N/A"),
      valueFormatter: ({ value }) => (value ? value : "N/A"),
    },
    cellRenderer: DocumentsUploadStatus,
  },
  {
    headerName: "PRS upload status",
    field: "upload_status",
    valueGetter: ({ data }) => {
      let value = data?.upload_status
      if (!value) {
        return "N/A"
      } else if (value.includes("ERROR")) {
        // string or array
        return "Error"
      }
      return value.replace("_", " ").replace(/\w\S*/g, (txt) => {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
      })
    },
    cellRenderer: PrsUploadStatusCell,
  },
  {
    headerName: "PRS record status",
    field: "record_status",
    valueGetter: ({ data }) => (data?.record_status ? data.record_status : "N/A"),
  },
  {
    headerName: "PRS Validation Messages",
    field: "validations",
    cellRenderer: "PrsValidationsWithCount",
    filter: false,
    comparator: (valueA, valueB) => {
      let aLen = valueA?.reduce((acc, item) => item.count + acc, 0) || 0
      let bLen = valueB?.reduce((acc, item) => item.count + acc, 0) || 0
      if (aLen == bLen) return 0
      return aLen > bLen ? 1 : -1
    },
  },
  {
    headerName: "Auto release?",
    field: "autorelease",
    filterParams: {
      keyCreator: ({ value }) => (value ? "Yes" : "No"),
      valueFormatter: ({ value }) => (value ? "Yes" : "No"),
    },
    cellRenderer: ({ value }) => {
      if (value && value === true) {
        return "Yes"
      } else if (value === false) {
        return "No"
      } else {
        return "N/A"
      }
    },
  },
  {
    headerName: "Release receipt",
    field: "receipt_url",
    filterParams: {
      keyCreator: ({ value }) => (value ? value : "N/A"),
      valueFormatter: ({ value }) => (value ? value : "N/A"),
    },
    cellRenderer: LinkCellRenderer,
  },
]

export {
  getSubmissionSchemas,
  getSubmissionFormSections,
  getSubmissionSectionTitles,
  getSubmissionRegistryTitles,
  REGISTRY_OPTIONS,
  registryContinentMap,
  FORM_VIEW_TRANSFORMER,
  FORM_SECTION_TRANSFORMER,
  submissionColDefs,
  getSubmissionFormColDefs,
}
